import { useRecoilCallback } from 'recoil'
import {
  caseImagesAtom,
  caseImageVariantPricingAtom,
  caseImageVariantsAtom,
} from '@/atoms/caseImageAtoms'
import { getCaseImageList, type CaseImageRecord } from '@/api/caseImages'
import {
  getCaseImageVariantPricing,
  getCaseImageVariantsForCase,
} from '@/api/caseImageVariants'
import { groupCaseImageVariants } from '@/utils/caseImages/groupCaseImageVariants'
import { useAiImageEditsKnownOff } from './useAiImageEditsEnabled'
import { logger, EventCategory } from 'utils/logger'

/**
 * The newest loads, shared by every instance of the hook because the atoms
 * they write are global: the case the variants atom belongs to, a number per
 * kind of load that grows with each request, and the newest image-list
 * request's reply. A reply is stored only while its request is still the
 * newest, so a slow reply for the case a person just left never overwrites
 * the case now open.
 */
const latestLoad: {
  variantsCaseId: string | null
  variantsRequest: number
  imagesRequest: number
  imagesReply: Promise<CaseImageRecord[]> | null
  pricingRequest: number
} = {
  variantsCaseId: null,
  variantsRequest: 0,
  imagesRequest: 0,
  imagesReply: null,
  pricingRequest: 0,
}

/**
 * What `loadCaseImagesWithVariants` resolves to: the image list, and the
 * reason the variants did not answer, or null when they did.
 */
export type CaseImagesWithVariants = {
  images: CaseImageRecord[]
  variantsError: Error | null
}

/** Start an image-list request as the newest one. */
const startImagesRequest = (caseId: string) => {
  const request = ++latestLoad.imagesRequest
  const reply = getCaseImageList(caseId)
  latestLoad.imagesReply = reply
  return { request, reply }
}

const asError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error))

/**
 * Loads what the case images grid needs. Opening a case's details from
 * the dashboard or the print package, an upload, delete or set-as-default in
 * the case details or the editor's photos menu, and opening that menu go
 * through `loadCaseImages`, and the reload after an Enhance accept, a revert
 * or its undo through `loadCaseImagesWithVariants`, so those loads also
 * refresh the variants (a revert mark included, since it rides on the
 * variants) and the pricing, which `loadCaseImages` skips once AI image
 * edits are known to be off. The editor's own loads
 * (`Editor.js`, `EditorToolbar.js`, `ImageItem.js`), `Auth.js` and
 * `PrintPackageNav.js` call `getCaseImageList` directly and refresh neither;
 * inside the editor, `EditorEnhanceProvider` fetches the variants alone
 * (`loadCaseImageVariants`) when its case changes while AI image edits are
 * on, so a canvas photo can tell a reverted enhancement from none.
 */
export const useCaseImageVariants = () => {
  const aiImageEditsKnownOff = useAiImageEditsKnownOff()

  /**
   * Fetch the case's variants in one request and store them grouped by
   * image. Asking for a different case first clears the variants held for
   * the previous one. Resolves to null once they answered, or to the reason
   * they did not; a failure leaves the current case's variants in place and
   * is logged.
   */
  const fetchCaseImageVariants = useRecoilCallback(
    ({ set }) =>
      async (caseId: string): Promise<Error | null> => {
        const request = ++latestLoad.variantsRequest
        if (latestLoad.variantsCaseId !== caseId) {
          latestLoad.variantsCaseId = caseId
          set(caseImageVariantsAtom, {})
        }
        try {
          const variants = await getCaseImageVariantsForCase(caseId)
          if (request === latestLoad.variantsRequest) {
            set(caseImageVariantsAtom, groupCaseImageVariants(variants))
          }
          return null
        } catch (error) {
          logger.warn({
            message: 'case_image_variants_load_failed',
            category: EventCategory.ERROR,
            error: error instanceof Error ? error : undefined,
            data: { caseId },
          })
          return asError(error)
        }
      },
    [],
  )

  /**
   * `fetchCaseImageVariants` for a caller that carries on without them:
   * the grid must keep working without enhancements, so a failure is only
   * logged.
   */
  const loadCaseImageVariants = useRecoilCallback(
    () => async (caseId: string) => {
      await fetchCaseImageVariants(caseId)
    },
    [fetchCaseImageVariants],
  )

  /**
   * Fetch the current pricing and store it, unless a newer pricing request
   * has started since: two overlapping case loads can answer out of order,
   * and the older price must not replace the newer one. A failure keeps
   * whatever price is already shown (or none) and is logged; the UI never
   * falls back to a hardcoded amount.
   */
  const refreshPricing = useRecoilCallback(
    ({ set }) =>
      async () => {
        const request = ++latestLoad.pricingRequest
        try {
          const pricing = await getCaseImageVariantPricing()
          if (request !== latestLoad.pricingRequest) return
          set(caseImageVariantPricingAtom, pricing)
        } catch (error) {
          logger.warn({
            message: 'case_image_variant_pricing_load_failed',
            category: EventCategory.ERROR,
            error: error instanceof Error ? error : undefined,
          })
        }
      },
    [],
  )

  /**
   * Fetch pricing only when none has loaded yet, for surfaces (the canvas
   * low-resolution tooltip) that need an amount without reloading images.
   */
  const loadPricing = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const current = snapshot.getLoadable(caseImageVariantPricingAtom)
        if (current.state === 'hasValue' && current.contents) return
        await refreshPricing()
      },
    [refreshPricing],
  )

  /**
   * Store a case's image list while its load is still the newest, and
   * resolve to the newest list. A load a newer one has overtaken stores
   * nothing and resolves to the newer load's list once that answers: the
   * newer list is at least as fresh, and a caller that refills the template
   * slots or moves the canvas from an older one would put back a file the
   * server no longer shows. A newer load that fails rejects this one too.
   */
  const settleImages = useRecoilCallback(
    ({ set }) =>
      async (
        request: number,
        images: CaseImageRecord[],
      ): Promise<CaseImageRecord[]> => {
        if (request === latestLoad.imagesRequest) {
          set(caseImagesAtom, images)
          return images
        }
        let answered = request
        let newest = images
        while (
          answered !== latestLoad.imagesRequest &&
          latestLoad.imagesReply
        ) {
          answered = latestLoad.imagesRequest
          newest = await latestLoad.imagesReply
        }
        return newest
      },
    [],
  )

  /**
   * Refresh the image list, and start its variants and pricing alongside.
   * Only the image list is awaited: opening a case (the dashboard, the print
   * package, the editor's photos menu) must not wait on the optional AI
   * requests, which each allow up
   * to 10 seconds and fail soft. Their results land in their atoms when they
   * answer. Pricing is fetched again on every call, so a price an M3 admin
   * changes shows the next time a case's images load, unless the switch has
   * answered off: then nothing shows a price, so none is asked for. While the
   * switch's answer is still unknown the price is asked for, so it has
   * already landed if the switch then reads on. The variants load either way: an
   * enhancement accepted while the switch was on keeps showing in the grid,
   * as it does in print. The image list failing
   * throws, for the caller to handle. A load that a newer one has overtaken
   * stores nothing and resolves to the newer load's list (`settleImages`).
   *
   * `clearOnFailure` is for opening a case: a failed load then empties the
   * list, so the case opens with no images rather than another case's. The
   * list is emptied only while this load is still the newest, so a failure
   * for the case a person just left never blanks the case now open. Without
   * it, a failed refresh keeps the images already shown.
   */
  const loadCaseImages = useRecoilCallback(
    ({ set }) =>
      async (
        caseId: string,
        { clearOnFailure = false }: { clearOnFailure?: boolean } = {},
      ) => {
        const { request, reply } = startImagesRequest(caseId)
        void loadCaseImageVariants(caseId)
        if (!aiImageEditsKnownOff) void refreshPricing()
        let images: CaseImageRecord[]
        try {
          images = await reply
        } catch (error) {
          if (clearOnFailure && request === latestLoad.imagesRequest) {
            set(caseImagesAtom, [])
          }
          throw error
        }
        return settleImages(request, images)
      },
    [aiImageEditsKnownOff, loadCaseImageVariants, refreshPricing, settleImages],
  )

  /**
   * `loadCaseImages` for a caller that reads the variants or the price right
   * after it resolves: the editor's Enhance from a canvas item, which opens
   * the disclosure on the tile's fresh variants, and the refresh after an
   * accept, a revert or its undo, whose flow must not close while the tile
   * still shows the old file. Resolves only once the image list, the
   * variants and the pricing have all answered, with the newest image list
   * (`settleImages`) and whether the variants answered (`variantsError`), so
   * a caller that acts on the variants can report their failure instead of
   * acting on none. The image list failing throws.
   */
  const loadCaseImagesWithVariants = useRecoilCallback(
    () =>
      async (caseId: string): Promise<CaseImagesWithVariants> => {
        const { request, reply } = startImagesRequest(caseId)
        const [images, variantsError] = await Promise.all([
          reply,
          fetchCaseImageVariants(caseId),
          refreshPricing(),
        ])
        return {
          images: await settleImages(request, images),
          variantsError,
        }
      },
    [fetchCaseImageVariants, refreshPricing, settleImages],
  )

  return {
    loadCaseImages,
    loadCaseImagesWithVariants,
    loadCaseImageVariants,
    loadPricing,
  }
}
